import React, { useMemo, useState } from "react";
import {
  Box,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import GaugeChart, { GAUGE_CHART_HEIGHT } from "./chartWidget/GaugeChart";
import ComboBarLineChart from "./chartWidget/ComboBarLineChart";
import BarChart from "./chartWidget/BarChart";
import { FormSectionCard } from "../layout/page";
import SimpleLoadingIndicator from "./simpleLoadingIndicator";

const TOP_INSURER_LIMIT = 12;
const numFormatter = Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 2,
});

const formatCompactCurrency = (value) =>
  numFormatter.format(Number(value) || 0);

const formatCompactCount = (value) =>
  numFormatter.format(Number(value) || 0);

const EMPTY_DASHBOARD = {
  summary: {
    confirmedPremium: 0,
    totalPremium: 0,
    renewedPolicies: 0,
    totalPolicies: 0,
    conversionRate: 0,
    nonRenewedCases: 0,
  },
  monthly: [],
  insurers: [],
  productTypes: [],
  insurersByPolicy: [],
};

const ChartMetricToggle = ({ value, onChange, options }) => (
  <SegmentedControl size="xs" value={value} onChange={onChange} data={options} />
);

const DashboardGiRenewalTab = ({
  giRenewalDashboard = EMPTY_DASHBOARD,
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [insuranceClassMetric, setInsuranceClassMetric] = useState("count");
  const [insurerMetric, setInsurerMetric] = useState("policyCount");

  const summary = giRenewalDashboard?.summary || EMPTY_DASHBOARD.summary;
  const monthly = giRenewalDashboard?.monthly || [];
  const productTypes = giRenewalDashboard?.productTypes || [];
  const insurersByPolicy = giRenewalDashboard?.insurersByPolicy || [];

  const insuranceClassToggleOptions = useMemo(
    () => [
      {
        label: t("dashboard.giRenewal.metricPolicies", "Total Policies"),
        value: "count",
      },
      {
        label: t("dashboard.giRenewal.metricPremium", "Gross Premium"),
        value: "premium",
      },
    ],
    [t]
  );

  const insurerToggleOptions = useMemo(
    () => [
      {
        label: t("dashboard.giRenewal.metricPolicies", "Total Policies"),
        value: "policyCount",
      },
      {
        label: t("dashboard.giRenewal.metricPremium", "Gross Premium"),
        value: "totalPremium",
      },
    ],
    [t]
  );

  const insuranceClassChartData = useMemo(() => {
    const valueKey =
      insuranceClassMetric === "premium" ? "totalPremium" : "count";
    return productTypes
      .map((item) => ({
        name: item.name,
        value: item[valueKey] ?? 0,
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => a.value - b.value);
  }, [productTypes, insuranceClassMetric]);

  const insurerChartData = useMemo(() => {
    if (insurerMetric === "policyCount") {
      return insurersByPolicy
        .slice(0, TOP_INSURER_LIMIT)
        .map((insurer) => ({
          name: insurer.name,
          value: insurer.policyCount ?? 0,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => a.value - b.value);
    }

    return insurersByPolicy
      .slice(0, TOP_INSURER_LIMIT)
      .map((insurer) => ({
        name: insurer.name,
        value: insurer.totalPremium ?? 0,
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => a.value - b.value);
  }, [insurersByPolicy, insurerMetric]);

  const monthlyChartData = useMemo(
    () =>
      monthly.map((item) => {
        const total = Number(item.totalPremium) || 0;
        const confirmed = Number(item.confirmedPremium) || 0;
        return {
          ...item,
          remainingPremium: Math.max(total - confirmed, 0),
        };
      }),
    [monthly]
  );

  const blueShades = [
    theme.colors.deepBlue?.[8] || "#1864ab",
    theme.colors.deepBlue?.[6] || "#1971c2",
    theme.colors.cyan?.[6] || "#15aabf",
    theme.colors.cyan?.[4] || "#3bc9db",
    theme.colors.cyan?.[2] || "#99e9f2",
  ];

  const totalPremiumMax = summary.totalPremium || 1;
  const totalPoliciesMax = summary.totalPolicies || 1;

  const insuranceClassTitle =
    insuranceClassMetric === "premium"
      ? t(
          "dashboard.giRenewal.insuranceClassByPremium",
          "Insurance Class Distribution (Total Gross Premium)"
        )
      : t(
          "dashboard.giRenewal.insuranceClassByPolicies",
          "Insurance Class Distribution (Total Policies)"
        );

  const insurerTitle =
    insurerMetric === "policyCount"
      ? t(
          "dashboard.giRenewal.insurerByPolicies",
          "Insurer Distribution (Total Policies)"
        )
      : t(
          "dashboard.giRenewal.insurerByPremium",
          "Insurer Distribution (Gross Premium)"
        );

  const formatInsurerValue = (value) =>
    insurerMetric === "totalPremium"
      ? formatCompactCurrency(value)
      : formatCompactCount(value);

  const formatInsuranceClassValue = (value) =>
    insuranceClassMetric === "premium"
      ? formatCompactCurrency(value)
      : formatCompactCount(value);

  return (
    <Box pos="relative" mt="50px">
      <SimpleLoadingIndicator visible={loading} />
      <Stack spacing="md">
        <SimpleGrid cols={4} spacing={16} breakpoints={[{ maxWidth: "md", cols: 1 }]}>
          <GaugeChart
            compact
            title={t(
              "dashboard.giRenewal.confirmedPremium",
              "Total Confirmed Premium"
            )}
            value={summary.confirmedPremium}
            max={totalPremiumMax}
            color={blueShades[1]}
            formatValue={formatCompactCurrency}
            formatMin={() => "0"}
            formatMax={formatCompactCurrency}
          />
          <GaugeChart
            compact
            title={t("dashboard.giRenewal.renewedPolicies", "Renewed Policies")}
            value={summary.renewedPolicies}
            max={totalPoliciesMax}
            color={blueShades[2]}
            formatValue={formatCompactCount}
            formatMin={() => "0"}
            formatMax={formatCompactCount}
          />
          <GaugeChart
            compact
            title={t(
              "dashboard.giRenewal.conversionRate",
              "Renewal Conversion Rate"
            )}
            value={summary.conversionRate}
            max={100}
            color={blueShades[0]}
            formatValue={(value) => `${Number(value || 0).toFixed(2)}%`}
            formatMin={() => "0%"}
            formatMax={() => "100%"}
          />
          <FormSectionCard
            title={t(
              "dashboard.giRenewal.nonRenewedCases",
              "Non-Renewed Cases"
            )}
            h="100%"
            mb={0}
            p={0}
            paperPadding="sm"
            titleMb={4}
          >
            <Box
              sx={{
                height: GAUGE_CHART_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Text size={28} weight={700}>
                {formatCompactCount(summary.nonRenewedCases)}
              </Text>
            </Box>
          </FormSectionCard>
        </SimpleGrid>

        <Stack spacing="md">
          <ComboBarLineChart
            title={t(
              "dashboard.giRenewal.monthDistribution",
              "Renewal Month Distribution"
            )}
            height={420}
            data={monthlyChartData}
            indexBy="label"
            barKeys={["confirmedPremium", "remainingPremium"]}
            barColors={[blueShades[1], theme.colors.gray?.[4] || "#ced4da"]}
            stacked
            topLabelKey="conversionRate"
            formatTopLabel={(value) => `${Number(value || 0).toFixed(2)}%`}
            chartProps={{
              margin: { top: 50, right: 40, bottom: 80, left: 60 },
              valueFormat: (value) => formatCompactCurrency(value),
              axisLeft: {
                format: (value) => formatCompactCurrency(value),
              },
              legends: [
                {
                  dataFrom: "keys",
                  anchor: "bottom",
                  direction: "row",
                  justify: false,
                  translateY: 60,
                  itemsSpacing: 16,
                  itemWidth: 140,
                  itemHeight: 20,
                  itemDirection: "left-to-right",
                  itemOpacity: 0.85,
                  symbolSize: 12,
                  data: [
                    {
                      id: "confirmedPremium",
                      label: t(
                        "dashboard.giRenewal.confirmedPremium",
                        "Total Confirmed Premium"
                      ),
                      color: blueShades[1],
                    },
                    {
                      id: "remainingPremium",
                      label: t(
                        "dashboard.giRenewal.remainingPremium",
                        "Unconfirmed Premium"
                      ),
                      color: theme.colors.gray?.[4] || "#ced4da",
                    },
                  ],
                },
              ],
            }}
          />

          <Box>
            <Group position="right" mb={4}>
              <ChartMetricToggle
                value={insuranceClassMetric}
                onChange={setInsuranceClassMetric}
                options={insuranceClassToggleOptions}
              />
            </Group>
            <BarChart
              title={insuranceClassTitle}
              height={Math.max(420, insuranceClassChartData.length * 52)}
              data={insuranceClassChartData}
              useSampleData={false}
              chartProps={{
                keys: ["value"],
                indexBy: "name",
                layout: "horizontal",
                colors: blueShades,
                padding: 0.12,
                enableLabel: false,
                valueFormat: formatInsuranceClassValue,
                margin: { top: 20, right: 40, bottom: 50, left: 180 },
                legends: [],
                axisLeft: {
                  format: (value) =>
                    value?.length > 18 ? `${value.substring(0, 18)}...` : value,
                },
                axisBottom: {
                  format: formatInsuranceClassValue,
                  tickValues: 5,
                },
                tooltip: ({ indexValue, value, color }) => (
                  <div
                    style={{
                      padding: "8px 12px",
                      background:
                        theme.colorScheme === "dark" ? "#25262b" : "#ffffff",
                      color:
                        theme.colorScheme === "dark"
                          ? theme.colors.gray[0]
                          : theme.colors.dark[8],
                      border: `1px solid ${
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[4]
                          : theme.colors.gray[3]
                      }`,
                      borderRadius: 6,
                      boxShadow: theme.shadows?.sm,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {indexValue}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: color,
                          display: "inline-block",
                        }}
                      />
                      {formatInsuranceClassValue(value)}
                    </div>
                  </div>
                ),
              }}
            />
          </Box>

          <Box>
            <Group position="right" mb={4}>
              <ChartMetricToggle
                value={insurerMetric}
                onChange={setInsurerMetric}
                options={insurerToggleOptions}
              />
            </Group>
            <BarChart
              title={insurerTitle}
              height={Math.max(520, insurerChartData.length * 52)}
              data={insurerChartData}
              useSampleData={false}
              chartProps={{
                keys: ["value"],
                indexBy: "name",
                layout: "horizontal",
                colors: blueShades,
                padding: 0.12,
                enableLabel: false,
                valueFormat: formatInsurerValue,
                margin: { top: 20, right: 40, bottom: 50, left: 180 },
                legends: [],
                axisLeft: {
                  format: (value) =>
                    value?.length > 18 ? `${value.substring(0, 18)}...` : value,
                },
                axisBottom: {
                  format: formatInsurerValue,
                  tickValues: 5,
                },
                tooltip: ({ indexValue, value, color }) => (
                  <div
                    style={{
                      padding: "8px 12px",
                      background:
                        theme.colorScheme === "dark" ? "#25262b" : "#ffffff",
                      color:
                        theme.colorScheme === "dark"
                          ? theme.colors.gray[0]
                          : theme.colors.dark[8],
                      border: `1px solid ${
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[4]
                          : theme.colors.gray[3]
                      }`,
                      borderRadius: 6,
                      boxShadow: theme.shadows?.sm,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {indexValue}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: color,
                          display: "inline-block",
                        }}
                      />
                      {formatInsurerValue(value)}
                    </div>
                  </div>
                ),
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DashboardGiRenewalTab;
